import React, { useState } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import { compose } from "redux";
import { createStructuredSelector } from "reselect";
import styled from "styled-components";
import { isEqual } from "lodash";

import { ExclamationCircleOutlined, LoadingOutlined } from "@ant-design/icons";
import { displayedMRN } from "@tailormed/common-client/util/mrn";
import { Button, Popover, Space, Spin, Tooltip, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";

import { starFilterList, starPatient } from "../../api/api";
import ActiveStarIcon from "../../assets/svg/ActiveStarIcon";
import EditPencilIcon from "../../assets/svg/EditPencilIcon";
import InActiveStarIcon from "../../assets/svg/InActiveStarIcon";
import { ReactComponent as AlertTrimmed } from "../../assets/svg/alert-circle-trimmed.svg";
import {
  BLACK,
  EXPRESS_GREEN,
  EXPRESS_HOVER,
  GRAY_400,
  GRAY_500,
  GRAY_900,
  PRIMARY_50,
  PRIMARY_600,
  PRIMARY_700,
  WHITE,
  GRAY_800
} from "../../constant/colors";
import { Routes } from "../../constant/routes";
import { fontWeights, sizes } from "../../constant/styles";
import { useGetSetting } from "../../hooks/getSetting";
import ErrorMessage from "../customComponent/customMessages/ErrorMessage";
import "./TooltipArrow.css";

import DrugsIcon from "../../assets/svg/DrugsIcon";
import { ReactComponent as HasConflictIcon } from "../../assets/svg/has-conflicts-icon.svg";
import { ReactComponent as UserIcon } from "../../assets/svg/user-icon.svg";
import { maxNumberOfRecords, selectedViewTypes } from "../../constant/table";
import {
  calculateTotalViewersCount,
  getViewersTooltipContent,
  parseViewers
} from "@tailormed/common-client/util/savedList";
import { SHARED, getTypeIcon } from "../../constant/savedList";
import { isExpressEnrollList } from "../../utils/savedList";
import ACTIONS from "../../store/action";
import {
  selectedViewTableData,
  selectSearchedPatients,
  selectPatientDetails,
  selectUser,
  selectUserOrGroupById,
  selectAllUsersAndUserGroups,
  selectUserGroupsWithUsers
} from "../../store/selector";
import { getStatusTagStyle, CLAIM_STATUS_COLORS } from "../../util/statusTagStyles";
import {
  useStandardizedTracking,
  EventNames,
  ContentTypes,
  LayoutTypes,
  FeatureGroups,
  PageKeys,
  EEContentKeys,
  EEOriginValues,
  EE_ENROLLMENT_ORIGIN_KEY
} from "../../utils/pendo/useStandardizedTracking";
import { selectFilters } from "../../store/selector";
import { defaultFilters } from "../../constant/filters";

import moment from "moment-timezone";
import { Link } from "react-router-dom";
import { statuses } from "../../constant/claim";
import { customerTypes } from "../../constant/customerType";
import { ASCEND, DECEND, SORT_DIRECTIONS_DEFAULT } from "../../constant/sort";
import { DATE_FORMAT, dateFormatter } from "../../utils/date";
import { formatAssignee, formatPhoneNumber, formatSSN } from "../../utils/formaters";
import { TitledTooltip } from "../TitledTooltip";
const { Text } = Typography;

const StyledStarButton = styled(Button)`
  padding: 0;
  margin-top: 5px;
  background-color: transparent !important;

  &:hover,
  &:focus,
  &:active {
    background-color: transparent !important;
  }
`;

const EnrollButton = styled(Link)`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 8px;
  background: ${EXPRESS_GREEN};
  color: ${WHITE};
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
  margin-right: 8px;

  &:hover {
    background: ${EXPRESS_HOVER};
    color: ${WHITE};
  }
`;

const EditButton = styled.div`
  background-color: ${PRIMARY_600};
  border-radius: 15px;
  height: 30px;
  width: 30px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: ${PRIMARY_700};
  }
`;

const LightningIcon = ({ width = 12, height = 12, stroke = "white", strokeWidth = 1 }) => (
  <svg width={width} height={height} viewBox="0 0 12 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M6.5 1L1 8H6L5.5 13L11 6H6L6.5 1Z"
      fill="transparent"
      stroke={stroke}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const HasConflictsColumn = ({ record }) => {
  const { t } = useTranslation();

  const hasConflicts = false;
  //record.hasDemographicsConflicts || record.hasDiagnosesConflicts || record.hasInsuranceConflicts;
  const history = useHistory();
  const { patientId, journeyId } = record;

  return (
    <div style={{ paddingTop: "8px", display: "flex", flexDirection: "column", gap: "5px" }}>
      {hasConflicts && (
        <Tooltip title={<Text>{t("view_conflicts")}</Text>} color={WHITE}>
          <div onClick={() => history.push(`/${Routes.PATIENT}${patientId}/journey/${journeyId}/edit`)}>
            <HasConflictIcon id={`patient-has-conflicts-${patientId}`} style={{ height: "17", width: "17" }} />
          </div>
        </Tooltip>
      )}
    </div>
  );
};

export const EditColumn = ({
  record,
  isHovering,
  showEnrollButton = false,
  isFilterListView = false,
  pageKey = PageKeys.ALL_PATIENTS
}) => {
  const { t } = useTranslation();
  const { track } = useStandardizedTracking();
  const filters = useSelector(selectFilters);
  const { patientId, journeyId, applicationId, papId } = record;

  const optimizationsPath = `/${Routes.PATIENT}${patientId}/journey/${journeyId}/optimizations`;

  const enrollmentPageKeys = [PageKeys.ALL_ENROLLMENTS, PageKeys.PENDING_RENEWALS];
  const isEnrollmentPage = enrollmentPageKeys.includes(pageKey);

  // Determine edit path and tooltip based on page type
  const getEditConfig = () => {
    if (isEnrollmentPage) {
      return {
        path: `/${Routes.PATIENT}${patientId}/journey/${journeyId}/applications/${papId}/${applicationId}/track?edit=true`,
        tooltipKey: "edit_enrollment_details",
        id: "enrollment_list_edit_enrollment"
      };
    }

    // Default: patient edit
    return {
      path: `/${Routes.PATIENT}${patientId}/journey/${journeyId}/edit`,
      tooltipKey: "edit_patient_details",
      id: "patient_list_edit_patient"
    };
  };

  const editConfig = getEditConfig();

  const effectivePageKey = isFilterListView ? PageKeys.FILTER_LIST_VIEW : pageKey;

  const getSourceFromPageKey = (key) => {
    const sourceMap = {
      [PageKeys.ALL_PATIENTS]: PageKeys.ALL_PATIENTS,
      [PageKeys.FILTER_LIST_VIEW]: "saved_list"
    };
    return sourceMap[key] || PageKeys.ALL_PATIENTS;
  };

  const handleEnrollClick = () => {
    sessionStorage.setItem(EE_ENROLLMENT_ORIGIN_KEY, EEOriginValues.ROW_ENROLL_BUTTON);

    const currentFilters = filters || {};
    const currentDefaultFilters = defaultFilters || {};

    const appliedFilters = Object.entries(currentFilters)
      .filter(
        ([key, value]) =>
          key !== "expressEnrollEligible" &&
          (currentDefaultFilters[key] === undefined || !isEqual(value, currentDefaultFilters[key]))
      )
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

    track(EventNames.BUTTON_CLICK, {
      pageKey: effectivePageKey,
      featureGroup: FeatureGroups.EXPRESS_ENROLLMENT,
      contentType: ContentTypes.BUTTON,
      contentKey: EEContentKeys.PATIENT_ROW_ENROLL_BTN,
      layout: LayoutTypes.TABLE,
      additionalInfo: {
        patient_id: patientId,
        applied_filters: appliedFilters,
        journey_id: journeyId,
        source: getSourceFromPageKey(effectivePageKey)
      }
    });
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: "8px",
        height: "100%",
        paddingRight: "16px",
        visibility: isHovering ? "visible" : "hidden",
        pointerEvents: isHovering ? "auto" : "none"
      }}
    >
      {showEnrollButton && (
        <Tooltip title={<Text>{t("enroll_express_tooltip")}</Text>} color={WHITE}>
          <EnrollButton
            id={`patient_list_enroll_express_${patientId}`}
            data-pendo="patient_list_enroll_express"
            to={{ pathname: optimizationsPath, state: { autoSelectEE: true } }}
            onClick={handleEnrollClick}
          >
            <LightningIcon width={24} height={24} stroke={WHITE} strokeWidth={1} />
            {t("enroll")}
          </EnrollButton>
        </Tooltip>
      )}
      <Link id={editConfig.id} data-pendo={editConfig.id} type="text" style={{ padding: 0 }} to={editConfig.path}>
        <Tooltip title={<Text>{t(editConfig.tooltipKey)}</Text>} color={WHITE}>
          <EditButton>
            <EditPencilIcon />
          </EditButton>
        </Tooltip>
      </Link>
    </div>
  );
};

export const PatientNameColumn = ({ oneLine, record }) => {
  const { t } = useTranslation();
  const [prefix, customerType] = useGetSetting(["id_prefix", "fe-customer-type"]);

  const patientColumnParentStyle = oneLine ? {} : { display: "flex", flexDirection: "column" };
  return (
    <div style={patientColumnParentStyle}>
      <Text
        style={{
          fontWeight: fontWeights.semibold,
          fontSize: sizes.medium,
          color: GRAY_900
        }}
        id="patient_name"
      >
        {record.name ? record.name : "Patient"}
      </Text>
      <span
        style={{
          display: oneLine ? "inline" : "flex",
          paddingLeft: oneLine ? "5px" : "",
          flexDirection: oneLine ? "" : "row",
          gap: oneLine ? "" : "5px",
          alignItems: oneLine ? "" : "center",
          lineHeight: oneLine ? "35px" : "",
          fontSize: oneLine ? sizes.medium : sizes.small,
          fontWeight: fontWeights.regular
        }}
      >
        {displayedMRN(record.mrn) && (
          <Text style={{ color: GRAY_500 }} id="patient_mrn">
            {customerType.toLowerCase() === customerTypes.RETAIL
              ? t("columns.record_phone", { mrnRecord: formatPhoneNumber(record.mrn) })
              : t("columns.record_mrn", { mrnRecord: displayedMRN(record.mrn) })}
          </Text>
        )}
        <Text
          style={{ color: GRAY_500, paddingLeft: oneLine && displayedMRN(record.mrn) ? "5px" : "" }}
          id="patient_id"
        >{`(${prefix ? prefix.toUpperCase() : "TM"}-${record.patientId})`}</Text>
      </span>
    </div>
  );
};

export const ClaimStatusColumn = ({ status, isActive }) => {
  const statusLabel = statuses.find((item) => item.value === status)?.label;
  const tagStyle = getStatusTagStyle(statusLabel, CLAIM_STATUS_COLORS);
  return (
    <Text
      style={{
        ...tagStyle,
        opacity: isActive === false ? "0.3" : undefined,
        cursor: "pointer"
      }}
    >
      {statusLabel}
    </Text>
  );
};

export const AssigneeColumn = ({ record, withoutResize = false }) => {
  const assigneesTextToDisplay = record?.assignees
    ?.map((assignee) => assignee.userName || assignee.assigneeName)
    .sort()
    .join(" | ");
  if (withoutResize) {
    return (
      <Tooltip title={<Text>{assigneesTextToDisplay}</Text>} color={WHITE} style={{ cursor: "pointer" }}>
        <Text ellipsis={true} style={{ maxWidth: "200px" }}>
          {assigneesTextToDisplay}
        </Text>
      </Tooltip>
    );
  }
  return <span>{assigneesTextToDisplay}</span>;
};

export const FacilityColumn = ({ record }) => {
  const facilitiesTextToDisplay =
    record?.facilities
      ?.map((facility) => facility?.name || "")
      ?.sort()
      ?.join(" | ") || "";
  return <span>{facilitiesTextToDisplay}</span>;
};

export const DateOfServiceColumn = ({ record }) => {
  return (
    <div style={{ whiteSpace: "nowrap" }}>
      {record.minServiceDate === record.maxServiceDate
        ? dateFormatter(record.minServiceDate)
        : `${dateFormatter(record.minServiceDate)} - ${dateFormatter(record.maxServiceDate)}`}
    </div>
  );
};

const EEBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${EXPRESS_GREEN};
  flex-shrink: 0;
`;

export const ListNameColumn = ({ record }) => {
  const { t } = useTranslation();
  const user = useSelector(selectUser);
  const isEEList = isExpressEnrollList(record);

  const getInvalidMessage = () => {
    const isOwner = user?.id === record?.user;
    if (isOwner) {
      return t("savedList.owner_in_valid_list");
    } else {
      return t("savedList.non_owner_in_valid_list");
    }
  };

  const typeIconData = getTypeIcon(record.type);
  const viewers = parseViewers(record.viewers);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        gap: "12px",
        alignItems: "center"
      }}
    >
      {typeIconData && (
        <Tooltip
          overlayClassName="custom-white-tooltip"
          arrow={{
            pointAtCenter: true
          }}
          overlayInnerStyle={{
            backgroundColor: WHITE,
            color: BLACK,
            borderRadius: 8,
            padding: "10px 15px 0 15px"
          }}
          title={
            <Space direction="vertical">
              <Text style={{ color: BLACK, fontWeight: fontWeights.semibold }}>{typeIconData.subtitle}</Text>
            </Space>
          }
        >
          <div style={{ height: "25px" }} id={typeIconData.id}>
            {typeIconData.icon}
          </div>
        </Tooltip>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: "0px" }}>
        <Space size={6}>
          {isEEList && (
            <Tooltip title={<Text>{t("express_enroll_saved_list")}</Text>} color={WHITE}>
              <EEBadge>
                <LightningIcon />
              </EEBadge>
            </Tooltip>
          )}
          <Text
            style={{
              fontWeight: fontWeights.semibold,
              fontSize: sizes.medium,
              color: GRAY_900
            }}
          >
            {record.name ? record.name : t("savedList.no_list_name")}
          </Text>
          {!record.isValid && (
            <Tooltip
              overlayInnerStyle={{
                backgroundColor: BLACK,
                borderRadius: 5,
                padding: "15px"
              }}
              overlayStyle={{
                maxWidth: "450px"
              }}
              autoAdjustOverflow={false}
              title={<Text style={{ color: WHITE, fontWeight: fontWeights.medium }}>{getInvalidMessage()}</Text>}
            >
              <div style={{ paddingTop: "1.5px", position: "relative", display: "flex", alignItems: "center" }}>
                <AlertTrimmed />
              </div>
            </Tooltip>
          )}
        </Space>

        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "25px" }}>
          <Text style={{ fontSize: sizes.medium, color: GRAY_500, fontWeight: fontWeights.regular }}>
            {t("savedList.description", {
              view: selectedViewTypes[record.selectedView].description
            })}
          </Text>
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <UserIcon />
            <Text style={{ fontSize: sizes.medium, color: GRAY_500, fontWeight: fontWeights.regular }}>
              {t("savedList.created_by")} {record.username}
            </Text>
          </div>

          {record.type === SHARED && viewers?.length > 0 && (
            <div id="shared_lists_viewers_tag">
              <ViewersTooltip viewers={viewers} owner={record.user} t={t} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const NumberOfRecordsColumn = ({ record, savedListsRecordCounts }) => {
  const { t } = useTranslation();
  const currentRecord = savedListsRecordCounts[record?.id];

  if (!currentRecord) return <Spin />;

  if (currentRecord.success === false) {
    return <Text> {t("savedList.no_records")} </Text>;
  } else if (currentRecord.success) {
    return (
      <Text>{currentRecord.total > maxNumberOfRecords ? t("savedList.more_than_10000") : currentRecord.total}</Text>
    );
  }

  return null;
};

const StarColumnComponent = ({
  record,
  isRecentPatient,
  patientDetails,
  updatePatientDetailsAct,
  setPatientTableRecordDataAct,
  updateSearchedPatientAct,
  updateRecentPatientAct
}) => {
  const [isFetching, setIsFetching] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const { t } = useTranslation();
  const title = t(record?.starred ? "favorite" : "mark_as_favorite");

  const handleStarPatient = async (patientId, newValue) => {
    try {
      setIsFetching(true);
      await starPatient(patientId, newValue);
      setPatientTableRecordDataAct(patientId, { starred: newValue });
      if (isRecentPatient) {
        updateRecentPatientAct(patientId, { starred: newValue });
      } else {
        updateSearchedPatientAct(patientId, { starred: newValue });
      }
      if (patientDetails && patientDetails.id === patientId) {
        updatePatientDetailsAct({ ...patientDetails, starred: newValue });
      }
    } catch (error) {
      ErrorMessage(`${t("errorMessages.patient_starred")} ${error}`);
    } finally {
      setIsFetching(false);
    }
  };

  return (
    <div
      onClick={(event) => event.stopPropagation()}
      className={`star-cell ${record.starred ? "starred" : "not-starred"}`}
      data-starred={record.starred ? "true" : "false"}
    >
      {isFetching && <Spin indicator={<LoadingOutlined style={{ fontSize: 18 }} spin />} />}
      {!isFetching && (
        <Button type="text" onClick={() => handleStarPatient(record.patientId, !record.starred)} style={{ padding: 0 }}>
          <Tooltip
            title={
              <Text style={{ color: BLACK, fontSize: sizes.small, fontWeight: fontWeights.semibold }}>{t(title)}</Text>
            }
            color={WHITE}
          >
            <div
              style={{
                minWidth: 18,
                minHeight: 18,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginTop: "-1px"
              }}
              id={record.starred ? "remove_from_favorite" : "mark_list_as_favorite"}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              {record.starred ? <ActiveStarIcon /> : <InActiveStarIcon color={isHovered ? GRAY_500 : GRAY_400} />}
            </div>
          </Tooltip>
        </Button>
      )}
    </div>
  );
};

const starColumnMapDispatchToProps = (dispatch) => ({
  updatePatientDetailsAct: (patientDetails) => dispatch(ACTIONS.updatePatientDetailsAction(patientDetails)),
  updateSearchedPatientAct: (patientId, sharedFields) =>
    dispatch(ACTIONS.updateSearchedPatient(patientId, sharedFields)),
  updateRecentPatientAct: (patientId, sharedFields) => dispatch(ACTIONS.updateRecentPatient(patientId, sharedFields)),
  setPatientTableRecordDataAct: (patientId, sharedFields) =>
    dispatch(ACTIONS.setPatientTableRecordData(patientId, sharedFields))
});
const starColumnMapStateToProps = createStructuredSelector({
  patientDetails: selectPatientDetails
});
const StarColumn = compose(connect(starColumnMapStateToProps, starColumnMapDispatchToProps))(StarColumnComponent);

export { StarColumn };

const StarFilterListColumn = ({ record, page }) => {
  const dispatch = useDispatch();
  const [isFetching, setIsFetching] = useState(false);
  const [localStarred, setLocalStarred] = useState(record.starred);
  const [isHovered, setIsHovered] = useState(false);
  const { t } = useTranslation();

  React.useEffect(() => {
    setLocalStarred(record.starred);
  }, [record.starred]);

  const title = t(localStarred ? "favorite" : "mark_as_favorite");

  const handleStarFilterList = async (filterListId, newValue) => {
    setLocalStarred(newValue);
    setIsFetching(true);

    try {
      const response = await starFilterList(filterListId, newValue);
      if (response && response.data && response.data.success) {
        dispatch(ACTIONS.searchSavedListAction(page));
      } else {
        setLocalStarred(!newValue);
        ErrorMessage(`${t("errorMessages.filterlist_starred")}`);
      }
    } catch (error) {
      setLocalStarred(!newValue);
      ErrorMessage(`${t("errorMessages.filterlist_starred")}`);
    } finally {
      setTimeout(() => {
        setIsFetching(false);
      }, 500);
    }
  };

  return (
    <>
      {isFetching && <Spin indicator={<LoadingOutlined style={{ fontSize: 18 }} spin />} />}
      {!isFetching && (
        <StyledStarButton
          type="text"
          id={record.starred ? "remove_from_favorite" : "mark_list_as_favorite"}
          onClick={() => handleStarFilterList(record.id, !localStarred)}
        >
          <Tooltip
            title={
              <Text style={{ color: BLACK, fontSize: sizes.small, fontWeight: fontWeights.semibold }}>{t(title)}</Text>
            }
            color={WHITE}
          >
            <div
              style={{ minWidth: 18, minHeight: 18 }}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              {localStarred ? <ActiveStarIcon /> : <InActiveStarIcon color={isHovered ? GRAY_500 : GRAY_400} />}
            </div>
          </Tooltip>
        </StyledStarButton>
      )}
    </>
  );
};

export { StarFilterListColumn };

export const ViewersTooltip = ({ viewers, owner, t }) => {
  const assigneesAndUserGroups = useSelector(selectAllUsersAndUserGroups);
  const userGroupsWithUsers = useSelector(selectUserGroupsWithUsers);

  const viewersArray = parseViewers(viewers);

  if (!viewersArray || viewersArray.length === 0) return null;

  const totalViewersCount = calculateTotalViewersCount(
    viewersArray,
    assigneesAndUserGroups,
    userGroupsWithUsers,
    owner
  );

  return (
    <Tooltip
      placement="top"
      overlayClassName="custom-white-tooltip"
      arrow={{
        pointAtCenter: true
      }}
      autoAdjustOverflow={true}
      overlayInnerStyle={{
        backgroundColor: WHITE,
        color: BLACK,
        borderRadius: 8,
        padding: "10px 15px 0 15px"
      }}
      title={
        <div style={{ color: BLACK }}>
          {getViewersTooltipContent(viewersArray, assigneesAndUserGroups, userGroupsWithUsers, owner)}
        </div>
      }
    >
      <div
        style={{
          borderRadius: 100,
          fontSize: sizes.small,
          fontWeight: fontWeights.bold,
          border: "none",
          backgroundColor: PRIMARY_50,
          color: GRAY_800,
          height: "28px",
          margin: "2px",
          padding: "5px 10px",
          alignItems: "center",
          display: "flex"
        }}
      >
        {totalViewersCount} {totalViewersCount === 1 ? t("savedList.viewer") : t("savedList.viewers")}
      </div>
    </Tooltip>
  );
};

export const DrugColumnComponent = ({ drugs }) => {
  const { t } = useTranslation();

  const associatedDrugs = drugs || "[]";
  if (drugs?.length === 1) {
    return <Text>{drugs[0]}</Text>;
  } else if (drugs?.length > 1) {
    let parsedDrugs = [];
    try {
      parsedDrugs = Array.isArray(associatedDrugs) ? associatedDrugs : JSON.parse(associatedDrugs);
    } catch (error) {
      // If JSON parsing fails, try splitting by comma
      parsedDrugs = typeof associatedDrugs === "string" ? associatedDrugs.split(",").map((drug) => drug.trim()) : [];
    }

    // Ensure parsedDrugs is always an array
    if (!Array.isArray(parsedDrugs)) {
      parsedDrugs = [parsedDrugs];
    }

    const tooltipContent = parsedDrugs
      .slice(0, 50)
      .sort((a, b) => a.localeCompare(b))
      .map((drug) => (
        <p
          key={drug}
          style={{
            fontFamily: "Open Sans",
            fontWeight: fontWeights.regular,
            fontSize: sizes.small,
            lineHeight: "150%",
            margin: 0,
            color: "black",
            display: "flex",
            alignItems: "flex-start",
            gap: "4px"
          }}
        >
          <span
            style={{
              fontSize: "18px",
              lineHeight: "1"
            }}
          >
            •
          </span>
          <Text>{drug}</Text>
        </p>
      ));
    return (
      <>
        {parsedDrugs.length === 0 ? (
          ""
        ) : parsedDrugs.length === 1 ? (
          parsedDrugs[0]
        ) : (
          <Tooltip
            title={parsedDrugs?.length && <div style={{ color: "black", padding: "0 10px" }}>{tooltipContent}</div>}
            color={WHITE}
            overlayStyle={{ borderRadius: "8px", maxWidth: "380px" }}
          >
            <Text style={{ marginRight: "auto" }}>
              <DrugsIcon /> {parsedDrugs?.length} {t("Drugs")}
            </Text>
          </Tooltip>
        )}
      </>
    );
  } else {
    return <Text></Text>;
  }
};

export const ClaimMedicationsColumnComponent = ({ record }) => {
  const { t } = useTranslation();
  const medications = record.claimMedications?.split(",") || [];

  return (
    <div style={{ textAlign: "center" }}>
      {medications.length === 0 ? (
        ""
      ) : medications.length === 1 ? (
        medications[0]
      ) : (
        <Popover
          trigger="hover"
          content={
            medications.length && (
              <div style={{ maxWidth: "400px", padding: "0 10px" }}>
                <Text>
                  {medications.slice(0, 50).join(", ")}
                  {medications.length > 50 && <span> ...</span>}
                </Text>
              </div>
            )
          }
        >
          <Text style={{ marginRight: "auto" }}>
            <DrugsIcon /> {medications.length} {t("Drugs")}
          </Text>
        </Popover>
      )}
    </div>
  );
};

export const withSorting = (sortingEnabled, tableColumn, { sorterTooltipText, ...rest }, sortDirection) => {
  const [threshold] = useGetSetting(["dashboard.sorting-threshold"]);
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const isSearchResultsPage = pathname.includes(Routes.SEARCH_RESULTS);
  const selectedViewData = useSelector(selectedViewTableData);
  const searchedPatientsData = useSelector(selectSearchedPatients);
  const sorter = isSearchResultsPage ? searchedPatientsData?.sorter : selectedViewData?.sorter;
  const sortingTooltipTitle = threshold && t("sortInfo", { threshold: threshold / 1000 });

  const getSortOrderForDisplay = (sorter, tableColumn) => {
    return sorter?.column?.key === tableColumn?.key ? sorter?.order : null;
  };

  const sortingActionTooltip =
    sorterTooltipText ||
    (() => {
      if (sorter?.column?.key === tableColumn.key) {
        if (sorter?.order === DECEND) {
          return t("columns.click_to_cancel_sorting");
        }
        if (sorter?.order === ASCEND) {
          return t("columns.click_to_sort_descending");
        }
      }
      return t("columns.click_to_sort_ascending");
    })();

  return {
    sorter: sortingEnabled,
    sortDirections: sortDirection ? sortDirection : SORT_DIRECTIONS_DEFAULT,
    sortOrder: getSortOrderForDisplay(sorter, tableColumn),
    ...tableColumn,
    title: () => (
      <div style={{ display: "flex", gap: "5px" }} id={`columnـ${tableColumn.dataIndex}`}>
        <Tooltip title={sortingActionTooltip}>
          <Text style={{ color: BLACK }}>{tableColumn.title}</Text>
        </Tooltip>
        <div>
          {!sortingEnabled && (
            <TitledTooltip text={sortingTooltipTitle}>
              <ExclamationCircleOutlined style={{ fontSize: "12px", alignSelf: "center", color: GRAY_400 }} />
            </TitledTooltip>
          )}
        </div>
      </div>
    ),
    showSorterTooltip: false,
    ...rest,
    defaultSortOrder: sorter?.column && sorter?.column?.key === tableColumn.key && sorter?.order
  };
};

export const getSortingTooltip = (t, sorter, columnKey) => {
  if (sorter?.columnKey === columnKey) {
    if (sorter?.order === DECEND) {
      return t("columns.click_to_cancel_sorting");
    }
    if (sorter?.order === ASCEND) {
      return t("columns.click_to_sort_descending");
    }
  }
  return t("columns.click_to_sort_ascending");
};

export const ViewedAtColumn = ({ record }) => {
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <Text
        style={{
          fontSize: sizes.medium,
          color: GRAY_900
        }}
      >
        {record.viewedAt ? moment.utc(record.viewedAt).local().format("MM/DD/YYYY h:mm a") : ""}
      </Text>
    </div>
  );
};

export const PatientDOBColumn = ({ record }) => {
  const { t } = useTranslation();

  if (!record.dob || record.dob === t("invalid_date")) {
    return null;
  }

  const formattedDob = moment(record.dob).isValid() ? moment(record.dob).format(DATE_FORMAT) : null;

  return formattedDob ? <div id="patient_dob">{formattedDob}</div> : null;
};

export const PatientSSNColumn = ({ record }) => {
  if (!record.social_security_number) {
    return null;
  }

  return <div id="patient_ssn">{formatSSN(record.social_security_number)}</div>;
};

export const PatientPhoneNumberColumn = ({ record }) => {
  if (!record.phone_number && !record.home_number) {
    return null;
  }

  return <div id="patient_phone_number">{formatPhoneNumber(record.phone_number || record.home_number)}</div>;
};

export const EnrollmentAssigneeColumn = ({ record }) => {
  if (!record?.enrollmentAssignee) return null;

  const getUserOrGroupById = useSelector(selectUserOrGroupById);
  const enrollmentAssigneeTextToDisplay = formatAssignee(getUserOrGroupById(record?.enrollmentAssignee));

  return <div id="patient_enrollment_assignee">{enrollmentAssigneeTextToDisplay}</div>;
};

export const ClaimAssigneeColumn = ({ record }) => {
  if (!record?.claimAssignee) return null;

  const getUserOrGroupById = useSelector(selectUserOrGroupById);
  const claimAssigneeTextToDisplay = formatAssignee(getUserOrGroupById(record?.claimAssignee));

  return <div id="patient_claim_assignee">{claimAssigneeTextToDisplay}</div>;
};

export const ClaimLastSavedByColumn = ({ record }) => {
  if (!record?.claimLastSavedBy) return null;

  const getUserOrGroupById = useSelector(selectUserOrGroupById);
  const claimLastSavedByTextToDisplay = formatAssignee(getUserOrGroupById(record?.claimLastSavedBy));

  return <div id="patient_claim_last_saved_by">{claimLastSavedByTextToDisplay}</div>;
};
